import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HomePage } from './pages/HomePage';
import { LearningPlatform } from './pages/LearningPlatform';
import { ProfilePage } from './pages/ProfilePage';
import { DashboardPage } from './pages/DashboardPage';
import { UserInfoPage } from './pages/UserInfoPage';
import { AdminDashboard } from './pages/dashboard/AdminDashboard';
import { ContentManagement } from './pages/admin/ContentManagement';
import { AdminTrafficUserActivityPage } from './pages/admin/AdminTrafficUserActivityPage';
import { TeacherApproval } from './pages/admin/TeacherApproval';
import { ContentManagerDashboard } from './pages/dashboard/ContentManagerDashboard';
import { TeacherDashboard } from './pages/dashboard/TeacherDashboard';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuthStore } from './store/authStore';
import { useTimingDelivery } from './hooks/useForegroundTiming';
import { Toaster } from 'react-hot-toast';
import { TopicsList } from './pages/learning/TopicsList';
import { TopicDetail } from './pages/learning/TopicDetail';
import { CourseListPage } from './pages/teacher/CourseListPage';
import { CourseStudentsPage } from './pages/teacher/CourseStudentsPage';
import { CourseAnalyticsPage } from './pages/teacher/CourseAnalyticsPage';
import { StudentLearningDetailPage } from './pages/teacher/StudentLearningDetailPage';
import { CourseEditPage } from './pages/teacher/CourseEditPage';
import {
  PrivilegedTopicLearnPage,
  StudentCourseLearnPage
} from './pages/student/StudentCourseLearnPage';
import {
  canTrackAuthenticatedRouteView,
  resetAuthenticatedRouteView,
  shouldResetAuthenticatedRouteView,
  trackAuthenticatedRouteView
} from './hooks/useEventTracking';

const AuthenticatedRouteViewObserver: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, token } = useAuthStore();
  const userId = user?.id;

  useEffect(() => {
    if (!canTrackAuthenticatedRouteView(isAuthenticated, userId, token, location.pathname)) {
      // Do not clear the navigation key while persisted auth is hydrating or
      // while an auth refresh has only updated part of the auth tuple.
      if (shouldResetAuthenticatedRouteView(
        isAuthenticated,
        userId,
        token,
        isLoading,
        useAuthStore.persist.hasHydrated()
      )) {
        resetAuthenticatedRouteView();
      }
      return;
    }

    // location.pathname is intentionally used instead of href/search/hash.
    void trackAuthenticatedRouteView(userId!, location.pathname);
  }, [isAuthenticated, isLoading, location.pathname, token, userId]);

  return null;
};

// Role-based redirect component
const RoleRedirect: React.FC<{ role: string }> = ({ role }) => {
  switch (role) {
    case 'admin':
    case 'super_admin':
      return <Navigate to="/admin/dashboard" replace />;
    case 'content_manager':
      return <Navigate to="/content-manager/dashboard" replace />;
    case 'teacher':
      return <Navigate to="/teacher/dashboard" replace />;
    case 'student':
    default:
      return <Navigate to="/student/dashboard" replace />;
  }
};

// Protected route component with role checking
const RoleRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles: string[];
}> = ({ children, allowedRoles }) => {
  const { user } = useAuthStore();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check both primaryRole and roles array with safe defaults
  const userPrimaryRole = user.primaryRole || user.roles?.[0]?.name || 'student';
  const hasAccess = (userPrimaryRole && allowedRoles.includes(userPrimaryRole)) ||
                   user.roles?.some((role: any) => role?.name && allowedRoles.includes(role.name)) ||
                   (user.isAdmin === true && allowedRoles.includes('admin'));

  if (!hasAccess) {
    // Redirect to appropriate dashboard based on user's actual role
    const primaryRole = userPrimaryRole || 'student';
    return <RoleRedirect role={primaryRole} />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  useTimingDelivery();
  const { isAuthenticated, user } = useAuthStore();

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        <AuthenticatedRouteViewObserver />
        <Routes>
          <Route path="/" element={<HomePage />} />

          {/* User Info Collection */}
          <Route
            path="/user-info"
            element={
              <ProtectedRoute>
                <UserInfoPage />
              </ProtectedRoute>
            }
          />

          {/* ==================== STUDENT ROUTES ==================== */}
          <Route path="/student" element={
            <RoleRoute allowedRoles={['student']}>
              <DashboardPage />
            </RoleRoute>
          } />
          <Route path="/student/dashboard" element={
            <RoleRoute allowedRoles={['student']}>
              <DashboardPage />
            </RoleRoute>
          } />
          <Route path="/student/learn" element={
            <RoleRoute allowedRoles={['student', 'admin', 'super_admin']}>
              <LearningPlatform />
            </RoleRoute>
          } />
          <Route path="/student/topics" element={
            <RoleRoute allowedRoles={['student']}>
              <TopicsList />
            </RoleRoute>
          } />
          <Route path="/student/topics/:slug" element={
            <RoleRoute allowedRoles={['student']}>
              <TopicDetail />
            </RoleRoute>
          } />
          <Route path="/student/course/:courseId" element={
            <RoleRoute allowedRoles={['student']}>
              <StudentCourseLearnPage />
            </RoleRoute>
          } />

          {/* ==================== SHARED LEARNING ROUTE ==================== */}
          <Route path="/learn" element={
            <ProtectedRoute>
              <LearningPlatform />
            </ProtectedRoute>
          } />
          <Route path="/learning/topic/:topicId" element={
            <RoleRoute allowedRoles={['admin', 'super_admin', 'content_manager', 'teacher']}>
              <PrivilegedTopicLearnPage />
            </RoleRoute>
          } />

          {/* Learning Topics Routes */}
          <Route path="/learning/topics" element={
            <ProtectedRoute>
              <TopicsList />
            </ProtectedRoute>
          } />
          <Route path="/learning/topics/:slug" element={
            <ProtectedRoute>
              <TopicDetail />
            </ProtectedRoute>
          } />

          {/* ==================== ADMIN ROUTES ==================== */}
          <Route path="/admin" element={
            <RoleRoute allowedRoles={['admin', 'super_admin']}>
              <AdminDashboard />
            </RoleRoute>
          } />
          <Route path="/admin/dashboard" element={
            <RoleRoute allowedRoles={['admin', 'super_admin']}>
              <AdminDashboard />
            </RoleRoute>
          } />
          <Route path="/admin/content" element={
            <RoleRoute allowedRoles={['admin', 'super_admin']}>
              <ContentManagement />
            </RoleRoute>
          } />
          <Route path="/admin/teacher-approval" element={
            <RoleRoute allowedRoles={['admin', 'super_admin']}>
              <TeacherApproval />
            </RoleRoute>
          } />
          <Route path="/admin/profile" element={
            <RoleRoute allowedRoles={['admin', 'super_admin']}>
              <ProfilePage />
            </RoleRoute>
          } />
          <Route path="/admin/traffic/users/:userId" element={
            <RoleRoute allowedRoles={['admin', 'super_admin']}>
              <AdminTrafficUserActivityPage />
            </RoleRoute>
          } />

          {/* ==================== CONTENT MANAGER ROUTES ==================== */}
          <Route path="/content-manager" element={
            <RoleRoute allowedRoles={['content_manager']}>
              <ContentManagerDashboard />
            </RoleRoute>
          } />
          <Route path="/content-manager/dashboard" element={
            <RoleRoute allowedRoles={['content_manager']}>
              <ContentManagerDashboard />
            </RoleRoute>
          } />
          <Route path="/content-manager/content" element={
            <RoleRoute allowedRoles={['content_manager']}>
              <ContentManagement />
            </RoleRoute>
          } />

          {/* ==================== TEACHER ROUTES ==================== */}
          <Route path="/teacher" element={
            <RoleRoute allowedRoles={['teacher']}>
              <TeacherDashboard />
            </RoleRoute>
          } />
          <Route path="/teacher/dashboard" element={
            <RoleRoute allowedRoles={['teacher']}>
              <TeacherDashboard />
            </RoleRoute>
          } />
          <Route path="/teacher/courses/:courseId" element={
            <RoleRoute allowedRoles={['teacher']}>
              <CourseEditPage />
            </RoleRoute>
          } />
          <Route path="/teacher/courses" element={
            <RoleRoute allowedRoles={['teacher']}>
              <CourseListPage />
            </RoleRoute>
          } />
          <Route path="/teacher/courses/:courseId/students" element={
            <RoleRoute allowedRoles={['teacher']}>
              <CourseStudentsPage />
            </RoleRoute>
          } />
          <Route path="/teacher/courses/:courseId/analytics" element={
            <RoleRoute allowedRoles={['teacher']}>
              <CourseAnalyticsPage />
            </RoleRoute>
          } />
          <Route path="/teacher/courses/:courseId/students/:studentId" element={
            <RoleRoute allowedRoles={['teacher']}>
              <StudentLearningDetailPage />
            </RoleRoute>
          } />

          {/* ==================== SHARED ROUTES ==================== */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />

          {/* Catch-all for unmatched routes - redirect based on user's primary role */}
          <Route
            path="*"
            element={
              isAuthenticated ? (
                user?.isAdmin ? (
                  <RoleRedirect role="admin" />
                ) : user?.roles?.[0]?.name ? (
                  <RoleRedirect role={user.roles[0].name} />
                ) : (
                  <Navigate to="/student/dashboard" replace />
                )
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'rgba(255, 255, 255, 0.9)',
              color: '#333',
              backdropFilter: 'blur(10px)',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#f87171',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </Router>
  );
};

export default App;
